import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

export interface NooraniGroupListRow {
  id: string;
  schoolId: string;
  schoolName: string;
  name: string;
  teacherId: string | null;
  teacherName: string | null;
  studentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface NooraniGroupStudentRow {
  id: string;
  admissionNo: string;
  name: string;
  className: string;
  sectionName: string;
}

export interface NooraniGroupDetail extends NooraniGroupListRow {
  students: NooraniGroupStudentRow[];
}

export interface NooraniGroupListParams {
  page?: number;
  limit?: number;
  search?: string;
  /** Org Admin/Super Admin only — narrows to one school. */
  schoolId?: string;
}

export interface CreateNooraniGroupPayload {
  /** Org Admin/Super Admin only — which school this group belongs to. */
  schoolId?: string;
  name: string;
  teacherId?: string;
}

export interface UpdateNooraniGroupPayload {
  name?: string;
  teacherId?: string | null;
}

export interface SyncNooraniGroupStudentsPayload {
  add?: string[];
  remove?: string[];
}

export interface NooraniTeacherPickerRow {
  id: string;
  name: string;
}

export interface NooraniClassPickerRow {
  classId: string;
  className: string;
  sectionId: string | null;
  sectionName: string | null;
}

export interface NooraniStudentPickerRow {
  id: string;
  admissionNo: string;
  name: string;
  className: string;
  sectionName: string;
  /** The Noorani group this student is currently in, if any (null = unassigned). */
  assignedGroupId: string | null;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export const NooraniGroupsApi = {
  list: async (params?: NooraniGroupListParams): Promise<PaginatedResult<NooraniGroupListRow>> =>
    unwrap(await api.get('/noorani-groups', { params })),

  get: async (id: string): Promise<NooraniGroupDetail> => unwrap(await api.get(`/noorani-groups/${id}`)),

  create: async (payload: CreateNooraniGroupPayload): Promise<NooraniGroupDetail> =>
    unwrap(await api.post('/noorani-groups', payload)),

  update: async (id: string, payload: UpdateNooraniGroupPayload): Promise<NooraniGroupDetail> =>
    unwrap(await api.patch(`/noorani-groups/${id}`, payload)),

  remove: async (id: string): Promise<{ id: string }> => unwrap(await api.delete(`/noorani-groups/${id}`)),

  syncStudents: async (id: string, payload: SyncNooraniGroupStudentsPayload): Promise<NooraniGroupDetail> =>
    unwrap(await api.post(`/noorani-groups/${id}/students`, payload)),

  teachersPicker: async (schoolId?: string): Promise<NooraniTeacherPickerRow[]> =>
    unwrap(await api.get('/noorani-groups/pickers/teachers', { params: schoolId ? { schoolId } : undefined })),

  classesPicker: async (schoolId?: string): Promise<NooraniClassPickerRow[]> =>
    unwrap(await api.get('/noorani-groups/pickers/classes', { params: schoolId ? { schoolId } : undefined })),

  studentsPicker: async (params: { schoolId?: string; classId?: string; sectionId?: string; search?: string }): Promise<NooraniStudentPickerRow[]> =>
    unwrap(await api.get('/noorani-groups/pickers/students', { params })),
};
